import type { Cents } from "../domain/money";
import type { CostType } from "../domain/proposedCostLine";
import type { Baseline, BaselineLine, ChangeOrder } from "../domain/baseline";

/**
 * Baseline comparison and budget control (Phase 11, spec §27) — pure and
 * deterministic.
 *
 * Answers three questions and keeps them separate:
 *
 *   1. what is AUTHORIZED?          baseline + approved change orders
 *   2. what does the plan say NOW?  the current working estimate
 *   3. what changed since approval, item by item, in domain terms?
 *
 * It computes no actual cost and touches no accounting: Phase 9C remains the
 * only authority on money actually spent.
 */

/** A live estimate line, reduced to what comparison needs. */
export interface CurrentLine {
  id: string;
  featureId: string | null;
  featureName: string;
  roomName: string;
  categoryId: string | null;
  costType: CostType | null;
  description: string;
  amountCents: Cents;
  quantityX100: number | null;
  quantityUnit: string | null;
  unitCostCents: Cents | null;
  productId: string | null;
  productName: string;
  assemblyComponentId: string | null;
  packageApplicationId: string | null;
}

export type ChangeType =
  | "added"
  | "removed"
  | "quantity"
  | "price"
  | "product"
  | "amount";

export interface ChangeFindingLine {
  /** Stable key so a finding can be selected, authorized and de-duplicated. */
  key: string;
  type: ChangeType;
  description: string;
  featureId: string | null;
  featureName: string;
  roomName: string;
  categoryId: string | null;
  costType: CostType | null;
  baselineLineId: string | null;
  sourceLineId: string | null;
  baselineAmountCents: Cents;
  currentAmountCents: Cents;
  deltaCents: Cents;
  quantityDeltaX100: number | null;
  quantityUnit: string | null;
  productId: string | null;
  productName: string;
  /** One plain sentence: what changed, in the user's terms. */
  explanation: string;
}

export interface BaselineComparison {
  findings: ChangeFindingLine[];
  addedCents: Cents;
  removedCents: Cents;
  changedCents: Cents;
  /** Σ of all findings — the raw difference between current and baseline. */
  netDeltaCents: Cents;
  /** Findings already authorized by an approved change order. */
  authorizedKeys: string[];
}

export interface BudgetControl {
  baselineScopeCents: Cents;
  baselineContingencyCents: Cents;
  /** The originally approved authorized budget. */
  baselineTotalCents: Cents;
  approvedChangeCents: Cents;
  /** baselineTotal + approved change orders. */
  authorizedBudgetCents: Cents;
  /** The live estimate + the frozen contingency (like-for-like with above). */
  currentWorkingCents: Cents;
  currentScopeCents: Cents;
  /** currentWorking − authorizedBudget: the unapproved difference. */
  unapprovedDeltaCents: Cents;
  /** Draft change orders, shown but never authorized. */
  draftChangeCents: Cents;
  approvedChangeOrderCount: number;
  draftChangeOrderCount: number;
}

/**
 * Budget control totals. Contingency is FROZEN at the baseline and is not
 * automatically consumed by change orders — consumption rules belong to a
 * later phase and would otherwise be invented here (spec §24).
 */
export function budgetControl(input: {
  baseline: Pick<Baseline, "scopeCents" | "contingencyCents" | "totalCents"> | null;
  changeOrders: Pick<ChangeOrder, "status" | "netDeltaCents">[];
  currentScopeCents: Cents;
  /** The project's CURRENT contingency (may itself have been edited). */
  currentContingencyCents: Cents;
}): BudgetControl {
  const approved = input.changeOrders.filter((c) => c.status === "approved");
  const drafts = input.changeOrders.filter((c) => c.status === "draft");
  const approvedChangeCents = approved.reduce((sum, c) => sum + c.netDeltaCents, 0);
  const draftChangeCents = drafts.reduce((sum, c) => sum + c.netDeltaCents, 0);
  const baselineScopeCents = input.baseline?.scopeCents ?? 0;
  const baselineContingencyCents = input.baseline?.contingencyCents ?? 0;
  const baselineTotalCents = input.baseline?.totalCents ?? 0;
  const authorizedBudgetCents = baselineTotalCents + approvedChangeCents;
  const currentWorkingCents = input.currentScopeCents + input.currentContingencyCents;
  return {
    baselineScopeCents,
    baselineContingencyCents,
    baselineTotalCents,
    approvedChangeCents,
    authorizedBudgetCents,
    currentWorkingCents,
    currentScopeCents: input.currentScopeCents,
    unapprovedDeltaCents: input.baseline === null ? 0 : currentWorkingCents - authorizedBudgetCents,
    draftChangeCents,
    approvedChangeOrderCount: approved.length,
    draftChangeOrderCount: drafts.length,
  };
}

/**
 * Compare the current estimate with a baseline, in DOMAIN terms.
 *
 * Matching is by lineage first (the same proposed line), then by the same
 * assembly component, then by feature + description — so a regenerated line
 * is recognised as the same scope rather than reported as a delete and an
 * add. Only differences a person would call a change are reported: a
 * recalculated internal field that does not move the money is not a change.
 */
export function compareToBaseline(
  baseline: Pick<Baseline, "id" | "lines">,
  current: CurrentLine[],
  options: { authorizedKeys?: Set<string> } = {},
): BaselineComparison {
  const findings: ChangeFindingLine[] = [];
  const usedCurrent = new Set<string>();

  const byLineId = new Map<string, CurrentLine>();
  const byComponent = new Map<string, CurrentLine>();
  const byFeatureDescription = new Map<string, CurrentLine>();
  for (const line of current) {
    byLineId.set(line.id, line);
    if (line.assemblyComponentId) byComponent.set(line.assemblyComponentId, line);
    byFeatureDescription.set(`${line.featureId ?? ""}::${line.description}`, line);
  }

  const matchFor = (frozen: BaselineLine): CurrentLine | null => {
    const candidates = [
      frozen.sourceLineId ? byLineId.get(frozen.sourceLineId) : undefined,
      frozen.assemblyComponentId ? byComponent.get(frozen.assemblyComponentId) : undefined,
      byFeatureDescription.get(`${frozen.featureId ?? ""}::${frozen.description}`),
    ];
    for (const candidate of candidates) {
      if (candidate && !usedCurrent.has(candidate.id)) return candidate;
    }
    return null;
  };

  for (const frozen of baseline.lines) {
    const match = matchFor(frozen);
    if (!match) {
      findings.push({
        key: `removed:${frozen.id}`,
        type: "removed",
        description: frozen.description,
        featureId: frozen.featureId,
        featureName: frozen.featureName,
        roomName: frozen.roomName,
        categoryId: frozen.categoryId,
        costType: frozen.costType,
        baselineLineId: frozen.id,
        sourceLineId: frozen.sourceLineId,
        baselineAmountCents: frozen.amountCents,
        currentAmountCents: 0,
        deltaCents: -frozen.amountCents,
        quantityDeltaX100: frozen.quantityX100 === null ? null : -frozen.quantityX100,
        quantityUnit: frozen.quantityUnit,
        productId: frozen.productId,
        productName: frozen.productName,
        explanation: `"${frozen.description}" was in the approved scope and is no longer in the plan.`,
      });
      continue;
    }
    usedCurrent.add(match.id);
    if (match.amountCents === frozen.amountCents && sameProduct(frozen, match) && sameQuantity(frozen, match)) {
      continue; // unchanged scope: nothing a person would call a change
    }
    const type = classify(frozen, match);
    findings.push({
      key: `changed:${frozen.id}`,
      type,
      description: match.description,
      featureId: match.featureId ?? frozen.featureId,
      featureName: match.featureName || frozen.featureName,
      roomName: match.roomName || frozen.roomName,
      categoryId: match.categoryId ?? frozen.categoryId,
      costType: match.costType ?? frozen.costType,
      baselineLineId: frozen.id,
      sourceLineId: match.id,
      baselineAmountCents: frozen.amountCents,
      currentAmountCents: match.amountCents,
      deltaCents: match.amountCents - frozen.amountCents,
      quantityDeltaX100:
        frozen.quantityX100 === null || match.quantityX100 === null ? null : match.quantityX100 - frozen.quantityX100,
      quantityUnit: match.quantityUnit ?? frozen.quantityUnit,
      productId: match.productId,
      productName: match.productName,
      explanation: explain(type, frozen, match),
    });
  }

  for (const line of current) {
    if (usedCurrent.has(line.id)) continue;
    findings.push({
      key: `added:${line.id}`,
      type: "added",
      description: line.description,
      featureId: line.featureId,
      featureName: line.featureName,
      roomName: line.roomName,
      categoryId: line.categoryId,
      costType: line.costType,
      baselineLineId: null,
      sourceLineId: line.id,
      baselineAmountCents: 0,
      currentAmountCents: line.amountCents,
      deltaCents: line.amountCents,
      quantityDeltaX100: line.quantityX100,
      quantityUnit: line.quantityUnit,
      productId: line.productId,
      productName: line.productName,
      explanation: `"${line.description}" is not in the approved scope.`,
    });
  }

  findings.sort((a, b) => Math.abs(b.deltaCents) - Math.abs(a.deltaCents) || a.key.localeCompare(b.key));

  // A change already authorized by an approved change order is no longer an
  // UNAPPROVED change: leaving it in the list would let the same difference
  // be authorized twice (and would double-count it in the budget).
  const authorizedKeys = options.authorizedKeys ?? new Set<string>();
  const unauthorized = findings.filter((f) => !authorizedKeys.has(f.key));
  return {
    findings: unauthorized,
    addedCents: sum(unauthorized.filter((f) => f.type === "added")),
    removedCents: sum(unauthorized.filter((f) => f.type === "removed")),
    changedCents: sum(unauthorized.filter((f) => f.type !== "added" && f.type !== "removed")),
    netDeltaCents: sum(unauthorized),
    authorizedKeys: [...authorizedKeys],
  };
}

function sum(findings: ChangeFindingLine[]): Cents {
  return findings.reduce((total, f) => total + f.deltaCents, 0);
}

function sameProduct(frozen: BaselineLine, current: CurrentLine): boolean {
  return (frozen.productId ?? null) === (current.productId ?? null);
}

function sameQuantity(frozen: BaselineLine, current: CurrentLine): boolean {
  return (frozen.quantityX100 ?? null) === (current.quantityX100 ?? null);
}

function classify(frozen: BaselineLine, current: CurrentLine): ChangeType {
  if (!sameProduct(frozen, current)) return "product";
  if (!sameQuantity(frozen, current)) return "quantity";
  if (
    frozen.unitCostCents !== null &&
    current.unitCostCents !== null &&
    frozen.unitCostCents !== current.unitCostCents
  ) {
    return "price";
  }
  return "amount";
}

function explain(type: ChangeType, frozen: BaselineLine, current: CurrentLine): string {
  const money = (cents: Cents) => formatCents(cents);
  switch (type) {
    case "product":
      return `Product changed from ${frozen.productName || "no product"} to ${current.productName || "no product"} (${money(frozen.amountCents)} → ${money(current.amountCents)}).`;
    case "quantity":
      return `Quantity changed from ${qty(frozen.quantityX100, frozen.quantityUnit)} to ${qty(current.quantityX100, current.quantityUnit ?? frozen.quantityUnit)} (${money(frozen.amountCents)} → ${money(current.amountCents)}).`;
    case "price":
      return `Unit cost changed from ${money(frozen.unitCostCents ?? 0)} to ${money(current.unitCostCents ?? 0)} per ${current.quantityUnit ?? frozen.quantityUnit ?? "unit"}.`;
    default:
      return `Cost changed from ${money(frozen.amountCents)} to ${money(current.amountCents)}.`;
  }
}

function qty(valueX100: number | null, unit: string | null): string {
  if (valueX100 === null) return "no quantity";
  return `${(valueX100 / 100).toLocaleString("en-US")} ${unit ?? ""}`.trim();
}

/** Local formatter (the engine never imports UI helpers). */
function formatCents(cents: Cents): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString("en-US")}.${String(abs % 100).padStart(2, "0")}`;
}
